"use client";

import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import Link from "next/link";
import { useEffect, useState, useRef } from "react";
import BlogPostCard from "./blog-card";
import BlogSkeleton from "./blog-skeleton";
import { useSearchParams, useRouter } from "next/navigation";
import { useDebounce } from "@/lib/query/hooks/use-debounce";

interface TransformedBlogPost {
  id: string;
  title: string;
  category: string;
  date: string;
  readTime: string;
  author: {
    name: string;
    role: string;
    avatar: string;
    expertise: string[];
    bio: string;
  };
  excerpt: string;
  coverImage: string;
  slug: string;
}

interface BlogContentProps {
  transformedBlogPosts: TransformedBlogPost[];
  totalPosts: number;
  currentPage: number;
  totalPages: number;
  shouldShowPagination: boolean;
  category?: string;
  // search?: string;
  startPost: number;
  endPost: number;
}

export default function BlogContent({
  transformedBlogPosts,
  totalPosts,
  currentPage,
  totalPages,
  shouldShowPagination,
  category,
  // search,
  startPost,
  endPost,
}: BlogContentProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || ""
  );
  const [isFocused, setIsFocused] = useState(false);
  const isInitialMount = useRef(true);

  // Debounce the search query for automatic search
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Move the functions into the client component
  const getActiveCategoryName = () => {
    if (!category) return "";

    // Remove dashes and capitalize first letter of each word
    return category
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const search = searchParams.get("search");

  // Automatic search when debounced query changes (but not on initial mount)
  useEffect(() => {
    // Skip the first render to prevent automatic search on page load
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only trigger if the debounced query is different from the current URL search
    const currentSearch = searchParams.get("search") || "";
    if (debouncedSearchQuery !== currentSearch) {
      const params = new URLSearchParams(searchParams);
      if (debouncedSearchQuery.trim()) {
        params.set("search", debouncedSearchQuery.trim());
      } else {
        params.delete("search");
      }
      params.delete("page"); // Reset to first page when searching

      router.push(`/blogs?${params.toString()}`);
    }
  }, [debouncedSearchQuery, router, searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Manual search button - triggers immediately
    const params = new URLSearchParams(searchParams);
    if (searchQuery.trim()) {
      params.set("search", searchQuery.trim());
    } else {
      params.delete("search");
    }
    params.delete("page"); // Reset to first page when searching

    router.push(`/blogs?${params.toString()}`);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    const params = new URLSearchParams(searchParams);
    params.delete("search");
    params.delete("page"); // Reset to first page when clearing search
    router.push(`/blogs?${params.toString()}`);
  };

  const buildPageUrl = (targetPage: number) => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (category) params.set("category", category);
    if (targetPage > 0) params.set("page", targetPage.toString());
    const queryString = params.toString();
    return `/blogs${queryString ? `?${queryString}` : ""}`;
  };
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [transformedBlogPosts]);


  return (
    <section className="pt-28 md:pt-36 pb-16">
      {isLoading ? (
        <BlogSkeleton />
      ) : (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header with Results Info and Search */}
          <div className="mb-12">
            {/* Title and Search Row */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
              <div className="mb-4 lg:mb-0">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  {category
                    ? `${getActiveCategoryName()} Blogs`
                    : "Latest Blogs"}
                </h2>
                

                {/* Results Info */}
                {totalPosts > 0 && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center text-sm text-gray-600 ">
                      {/* <svg className="w-4 h-4 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg> */}
                      <span>
                        Showing{" "}
                        <span className="font-semibold text-indigo-600">
                          {startPost}-{endPost}
                        </span>{" "}
                        of{" "}
                        <span className="font-semibold text-gray-900">
                          {totalPosts}
                        </span>{" "}
                        {totalPosts === 1 ? "post" : "posts"}
                      </span>
                    </div>

                    {/* Clear Search Button */}
                    {/* {search && (
                                    <button
                                        onClick={handleClearSearch}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 transition-all duration-200 shadow-sm"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                        Clear Search
                                    </button>
                                )} */}
                  </div>
                )}
              </div>

              {/* Search Input */}
              <div className="w-full lg:w-96">
                <form onSubmit={handleSearch}>
                  <div
                    className={`relative overflow-hidden rounded-full  border `}
                  >
                    <div className="flex items-center">
                      <div className="absolute left-5 text-gray-400">
                        <Search
                          size={20}
                          strokeWidth={2}
                          className={`transition-colors ${
                            isFocused ? "text-indigo-500" : ""
                          }`}
                        />
                      </div>

                      <input
                        type="text"
                        placeholder="Search articles"
                        className="w-full py-3 pl-14 pr-20 bg-white focus:outline-none text-gray-700 text-sm"
                        value={searchQuery}
                        onChange={handleInputChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                      />

                      {/* Clear button inside input */}
                      {/* {searchQuery && (
                                                <button
                                                    type="button"
                                                    onClick={handleClearSearch}
                                                    className="absolute right-16 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                                    aria-label="Clear search"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            )} */}

                      <div className="absolute right-2">
                        <button
                          type="submit"
                          className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-4 py-1.5 rounded-full transition-colors shadow-sm text-sm"
                        >
                          Search
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Blog Posts Grid */}
          {transformedBlogPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {transformedBlogPosts.map((post, index) => (
                <BlogPostCard key={post.id} post={post} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="max-w-md mx-auto">
                <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No articles found
                </h3>
                <p className="text-gray-600 mb-6">
                  {search
                    ? `No articles match your search for "${search}".`
                    : category
                    ? `No articles found in the "${getActiveCategoryName()}" category.`
                    : "No articles are currently available."}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {category && (
                    <Link href="/blogs">
                      <Button variant="outline" className="w-full sm:w-auto">
                        View All Articles
                      </Button>
                    </Link>
                  )}
                  <Link href="/blogs">
                    <Button variant="outline" className="w-full sm:w-auto">
                      Browse All Articles
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Pagination */}
          {shouldShowPagination && (
            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-2 bg-white rounded-lg shadow-sm border border-gray-200 p-2">
                {/* Previous Button */}
                {currentPage > 0 && (
                  <Link href={buildPageUrl(currentPage - 1)}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center space-x-2  h-8"
                    >
                      <ChevronLeft size={16} />
             
                    </Button>
                  </Link>
                )}

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i;
                    } else if (currentPage < 3) {
                      pageNum = i;
                    } else if (currentPage >= totalPages - 3) {
                      pageNum = totalPages - 5 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Link key={pageNum} href={buildPageUrl(pageNum)}>
                        <Button
                          variant={
                            pageNum === currentPage ? "default" : "outline"
                          }
                          size="sm"
                          className="w-8 h-8 p-0"
                        >
                          {pageNum + 1}
                        </Button>
                      </Link>
                    );
                  })}
                </div>

                {/* Next Button */}
                {currentPage < totalPages - 1 && (
                  <Link href={buildPageUrl(currentPage + 1)}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex justify-center items-center space-x-1  h-8"
                    >
                    
                      <ChevronRight size={16} className="mt[]" />
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
